/**
 * PlatformBrandingContext — identitas Platform SaaS (BUKAN tenant/toko).
 *
 * Source: GET /api/platform (public, tanpa auth).
 * Digunakan di halaman platform (/platform/*):
 *   - Login page (nama, tagline, logo)
 *   - Sidebar (nama platform, versi, copyright)
 *   - Landing page (nantinya) — Create Tenant Wizard, Support widget
 *
 * ⚠️ JANGAN mencampur dengan tenant branding (BrandingContext.jsx).
 *    Tenant branding = branding untuk 1 toko/customer.
 *    Platform branding = identitas platform SaaS itu sendiri.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { imageUrl } from "@/lib/api";
import { useHost } from "@/context/HostContext";

const DEFAULT = {
  platform_name: "Dynicty",
  platform_tagline: "BUILD • GROW • EMPOWER",
  platform_description: "Dynicty — Platform All-in-One untuk Website, POS, CRM, Inventory, Marketing dan AI dalam satu platform terintegrasi.",
  platform_version: "1.0.0",
  platform_logo: "",
  platform_favicon: "",
  platform_primary_color: "#00D4FF",
  platform_secondary_color: "#8A2BE2",
  support_email: "",
  support_whatsapp: "",
  website_url: "https://app.dynicty.com",
  copyright_text: "© Dynicty",
  platform_status: "operational",
};

const PlatformBrandingContext = createContext({ platform: DEFAULT, loading: true, reload: async () => {} });

export function PlatformBrandingProvider({ children }) {
  const [platform, setPlatform] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const host = useHost();
  // HOTFIX P0 — Platform Branding Synchronization.
  // Platform Owner Portal (host = platform / dev preview) memakai identitas
  // Platform (Dynicty) untuk <title> + favicon. Pada tenant host, biarkan
  // tenant BrandingContext yang menguasai document meta.
  const isPlatformHost = host?.kind !== "tenant";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/platform");
      if (r.data) setPlatform((cur) => ({ ...cur, ...r.data }));
    } catch { /* keep defaults */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Inject platform primary color sebagai CSS variable khusus halaman platform.
  // Tidak override --brand-primary (yang dipakai tenant), pakai var terpisah
  // --platform-primary yang hanya dipakai komponen platform.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.style.setProperty("--platform-primary", platform.platform_primary_color || "#00D4FF");
    root.style.setProperty("--platform-secondary", platform.platform_secondary_color || "#8A2BE2");
    // Dynicty gradient palette (cyan → blue → indigo → purple)
    root.style.setProperty("--dynicty-cyan", "#00D4FF");
    root.style.setProperty("--dynicty-blue", "#1E7BFF");
    root.style.setProperty("--dynicty-indigo", "#5B5BFF");
    root.style.setProperty("--dynicty-purple", "#8A2BE2");
    root.style.setProperty("--dynicty-bg", "#08111F");
    root.style.setProperty("--dynicty-sidebar", "#0D1628");
    root.style.setProperty("--dynicty-card", "#121D33");
    root.style.setProperty("--dynicty-border", "#26334F");
    root.style.setProperty("--dynicty-text-primary", "#FFFFFF");
    root.style.setProperty("--dynicty-text-secondary", "#B5C3E0");
    root.style.setProperty(
      "--dynicty-gradient",
      "linear-gradient(135deg, #00D4FF 0%, #1E7BFF 40%, #5B5BFF 70%, #8A2BE2 100%)"
    );
  }, [platform.platform_primary_color, platform.platform_secondary_color]);

  // HOTFIX P0 — apply document.title & favicon dari Platform Branding pada
  // platform host (BUKAN tenant host). Menjamin browser tab menampilkan nama
  // + favicon Platform (Dynicty), bukan tenant (Sinar Telur). Effect ini
  // sengaja dijalankan setelah `platform` state terisi supaya menang atas
  // fallback statik dari index.html <title>.
  useEffect(() => {
    if (typeof document === "undefined" || !isPlatformHost) return;
    const name = (platform.platform_name || "").trim();
    if (name) document.title = name;

    const fav = platform.platform_favicon;
    if (fav) {
      const href = imageUrl(fav);
      // Bersihkan link icon lama supaya browser meng-cache favicon baru
      document.querySelectorAll("link[rel~='icon'], link[rel='shortcut icon']").forEach((el) => el.parentNode.removeChild(el));
      const link = document.createElement("link");
      link.rel = "icon";
      // cache-buster ringan agar tab langsung refresh favicon setelah upload
      const v = encodeURIComponent(fav);
      link.href = href + (href.includes("?") ? "&" : "?") + "v=" + v.slice(-16);
      document.head.appendChild(link);
    }

    // Theme color mengikuti platform primary
    let themeMeta = document.querySelector("meta[name='theme-color']");
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.name = "theme-color";
      document.head.appendChild(themeMeta);
    }
    themeMeta.content = platform.platform_primary_color || "#08111F";
  }, [isPlatformHost, platform.platform_name, platform.platform_favicon, platform.platform_primary_color]);

  const value = useMemo(() => ({ platform, loading, reload: load }), [platform, loading, load]);
  return <PlatformBrandingContext.Provider value={value}>{children}</PlatformBrandingContext.Provider>;
}

export function usePlatformBranding() { return useContext(PlatformBrandingContext); }
